import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useAsync, useMountedState } from 'react-use';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { pick } from 'lodash';

import { FormErrors } from '@mssgme/ui';
import { useFrontAPI, useInputTheme } from '../hooks';
import { PayButton } from './PayButton';

import styles from './StripeCheckoutForm.scss';

const CARD_OPTIONS = {
    hidePostalCode: true,
    iconStyle: 'solid',
    classes: {
        base: styles.cardBase,
        webkitAutofill: styles.cardAutofill,
    },
};

const propTypes = {
    orderId: PropTypes.string.isRequired,
    onPay: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

function StripeCheckoutForm({ orderId, disabled: rawDisabled, onPay }) {
    const stripe = useStripe();
    const elements = useElements();
    const api = useFrontAPI();
    const isMounted = useMountedState();
    const inputStyles = useInputTheme();
    const [succeeded, setSucceeded] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const [error, setError] = useState(null);

    const { style, options } = useMemo(
        () => ({
            options: {
                ...CARD_OPTIONS,
                disabled: processing || succeeded,
                style: {
                    base: {
                        iconColor: inputStyles.input.color,
                        color: inputStyles.input.color,
                        // ...pick(inputStyles.input, ['backgroundColor', 'color']),
                        '::placeholder': pick(inputStyles.input, ['backgroundColor', 'color']),
                        '::selection': { backgroundColor: 'currentColor' },
                        ':-webkit-autofill': {
                            color: inputStyles.input.color,
                        },
                    },
                },
            },
            style: {
                ...inputStyles.input,
                overflow: 'hidden',
            },
        }),
        [processing, succeeded, inputStyles.input]
    );

    const { value: clientSecret } = useAsync(
        async () => (await api.orders.signatures.createStripePaymentIntent({ orderId })).clientSecret,
        [orderId]
    );

    const isDisabled = processing || disabled || succeeded || rawDisabled || !clientSecret;

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setProcessing(true);

            try {
                const payload = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: {
                        card: elements.getElement(CardElement),
                    },
                });

                setError(payload.error ? `Payment failed ${payload.error.message}` : null);

                if (!payload.error) {
                    setSucceeded(true);

                    onPay('stripe', payload);
                }
            } finally {
                if (isMounted()) {
                    setProcessing(false);
                }
            }
        },
        [stripe, elements, clientSecret, onPay]
    );

    const handleChange = useCallback(async (e) => {
        setDisabled(e.empty || !!e.error);
        setError(e.error ? e.error.message : '');
    });

    return (
        <form onSubmit={handleSubmit}>
            <div style={style}>
                <CardElement onChange={handleChange} options={options} />
            </div>
            {error && <FormErrors error={error} shake errorStyle={inputStyles.error} />}

            <PayButton disabled={isDisabled} provider="stripe" style={{ marginTop: 16 }} contentAlignLeft={false} />
        </form>
    );
}

StripeCheckoutForm.propTypes = propTypes;

export default StripeCheckoutForm;
